import { useMemo, useState } from 'react'
import {
  analyzeMeans,
  blockingReasons,
  validateMeanGroups,
  verdictOf,
  type AnalysisSettings,
  type MeanAnalysisResult,
  type MeanGroupInput,
  type MeanVariantResult,
  type SequentialContext,
} from '../lib/stats/analysis'
import {
  formatAlpha,
  formatConfidence,
  formatCount,
  formatInterval,
  formatLift,
  formatMeasure,
  formatPValue,
  formatPercent,
  VERDICT_LABELS,
} from '../lib/format'
import { buildMeanReport } from '../lib/report'
import { defaultGroupName, encodeMeanState } from '../lib/urlState'
import { IssueList } from './IssueList'
import { IntervalChart, type ChartRow, type ChartTone } from './IntervalChart'
import { InfoTip } from './InfoTip'
import { NumberField } from './NumberField'
import { ResultGuard } from './ResultGuard'
import { SequentialCard, TestSettingsCard } from './TestSettings'

interface Props {
  groups: MeanGroupInput[]
  onGroupsChange: (groups: MeanGroupInput[]) => void
  settings: AnalysisSettings
  onSettingsChange: (settings: AnalysisSettings) => void
}

const METRIC_HINTS = {
  mean:
    'この群の平均です。下の小さい数字は対照群の平均です。' +
    '合計ではなく 1 人あたりの値を入れてください。',
  absolute:
    '比較群の平均から対照群の平均を引いた差です。単位は元の指標のままです。' +
    '下の括弧は信頼区間で、これが 0 をまたいでいる間は差があるとは言えません。',
  relative:
    '対照群を基準にした増減率です。比の分布は右に歪むため、' +
    '区間は対数変換して求めています。平均が 0 以下の指標では計算できません。',
  pValue:
    '本当は差がないのに、観測された差以上のものが偶然出てしまう確率です。' +
    '等分散を仮定しない Welch の t 検定で求めています。' +
    '有意水準（1 − 信頼水準）を下回ると有意と判定します。',
  df:
    'Welch-Satterthwaite の自由度です。両群のばらつきと人数から決まり、整数にはなりません。' +
    'これが小さいほど t 分布の裾が厚くなり、同じ差でも有意になりにくくなります。',
} as const

export function MeansView({
  groups,
  onGroupsChange,
  settings,
  onSettingsChange,
}: Props) {
  const [shareStatus, setShareStatus] = useState<string | null>(null)
  const [fallbackText, setFallbackText] = useState<string | null>(null)

  const validationErrors = useMemo(() => validateMeanGroups(groups), [groups])
  const analysis = useMemo(
    () =>
      validationErrors.length === 0 ? analyzeMeans(groups, settings) : null,
    [groups, settings, validationErrors],
  )

  const errorFor = (groupId: string, field: 'n' | 'mean' | 'sd') =>
    validationErrors.find((e) => e.groupId === groupId && e.field === field)
      ?.message

  const updateGroup = (id: string, patch: Partial<MeanGroupInput>) => {
    onGroupsChange(groups.map((g) => (g.id === id ? { ...g, ...patch } : g)))
  }

  const addGroup = () => {
    onGroupsChange([
      ...groups,
      {
        id: `m${Date.now()}`,
        name: defaultGroupName(groups.length),
        n: 1000,
        mean: 1000,
        sd: 800,
      },
    ])
  }

  const notify = (message: string) => {
    setShareStatus(message)
    window.setTimeout(() => setShareStatus(null), 4000)
  }

  const shareUrl = () =>
    `${window.location.origin}${window.location.pathname}?${encodeMeanState({
      groups: groups.map((g) => ({
        name: g.name,
        n: g.n,
        mean: g.mean,
        sd: g.sd,
      })),
      confidence: settings.confidence,
      tail: settings.tail,
      correction: settings.correction,
      sequential: settings.sequential,
    })}`

  const share = async () => {
    const url = shareUrl()
    try {
      await navigator.clipboard.writeText(url)
      setFallbackText(null)
      notify('リンクをコピーしました')
    } catch {
      window.history.replaceState(null, '', url)
      notify('URL を更新しました（アドレスバーからコピーしてください）')
    }
  }

  const copyReport = async () => {
    if (!analysis) return
    const text = buildMeanReport({
      analysis,
      settings,
      format: 'markdown',
      url: shareUrl(),
    })
    try {
      await navigator.clipboard.writeText(text)
      setFallbackText(null)
      notify('Markdown 形式でコピーしました')
    } catch {
      setFallbackText(text)
      notify('コピーできませんでした。下の本文を選択してください')
    }
  }

  return (
    <>
      <section className="card">
        <h2 className="card-title">観測データ</h2>
        <p className="card-hint">
          客単価・平均滞在時間・1 人あたり PV のように、1 件ずつの値が数えられる指標を比較します。
          先頭の群が対照群です。人数はその指標を観測した対象の数、標準偏差は
          <strong>不偏（分母 n − 1）</strong>のものを入れてください。
        </p>

        <div className="groups groups-means">
          <div className="groups-head-row" aria-hidden="true">
            <div className="groups-head">群の名前</div>
            <div className="groups-head groups-head-numeric">人数</div>
            <div className="groups-head groups-head-numeric">平均</div>
            <div className="groups-head groups-head-numeric">標準偏差</div>
            <div className="groups-head" />
          </div>

          {groups.map((group, index) => (
            <div className="group-row" key={group.id}>
              <div className="group-cell group-cell-name">
                <span
                  className={`group-badge ${index === 0 ? 'group-badge-control' : ''}`}
                >
                  {index === 0 ? '対照群' : '比較群'}
                </span>
                <input
                  type="text"
                  value={group.name}
                  aria-label="群の名前"
                  onChange={(event) =>
                    updateGroup(group.id, { name: event.target.value })
                  }
                />
              </div>
              <div className="group-cell">
                <span className="group-cell-label">人数</span>
                <NumberField
                  ariaLabel={`${group.name} の人数`}
                  value={group.n}
                  min={2}
                  step={1}
                  error={errorFor(group.id, 'n')}
                  onChange={(n) => updateGroup(group.id, { n })}
                />
              </div>
              <div className="group-cell">
                <span className="group-cell-label">平均</span>
                <NumberField
                  ariaLabel={`${group.name} の平均`}
                  value={group.mean}
                  step="any"
                  error={errorFor(group.id, 'mean')}
                  onChange={(mean) => updateGroup(group.id, { mean })}
                />
              </div>
              <div className="group-cell">
                <span className="group-cell-label">標準偏差</span>
                <NumberField
                  ariaLabel={`${group.name} の標準偏差`}
                  value={group.sd}
                  min={0}
                  step="any"
                  error={errorFor(group.id, 'sd')}
                  onChange={(sd) => updateGroup(group.id, { sd })}
                />
              </div>
              <div className="group-cell group-cell-action">
                {groups.length > 2 && index > 0 && (
                  <button
                    type="button"
                    className="button button-ghost"
                    onClick={() =>
                      onGroupsChange(groups.filter((g) => g.id !== group.id))
                    }
                    aria-label={`${group.name} を削除`}
                    title="この群を削除"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="groups-actions">
          <button type="button" className="button" onClick={addGroup}>
            + 比較群を追加
          </button>
        </div>
      </section>

      <TestSettingsCard
        settings={settings}
        onSettingsChange={onSettingsChange}
      />

      <SequentialCard
        settings={settings}
        onSettingsChange={onSettingsChange}
        sequential={analysis?.sequential ?? null}
      />

      {validationErrors.length > 0 && (
        <IssueList
          issues={[
            {
              level: 'error',
              title: '入力を確認してください',
              detail: '赤くなっている欄を直すと結果が表示されます。',
            },
          ]}
        />
      )}

      {analysis && (
        <>
          <IssueList issues={analysis.issues} />

          <ResultGuard
            blocked={analysis.resultsBlocked}
            reasons={blockingReasons(analysis.issues)}
          >
            <h2 className="section-title">判定</h2>
            {analysis.variants.map((variant) => (
              <MeanVerdictCard
                key={variant.group.id}
                variant={variant}
                controlName={analysis.control.group.name}
                settings={settings}
                sequential={analysis.sequential}
                correctionApplied={
                  settings.correction !== 'none' && analysis.comparisonCount > 1
                }
              />
            ))}

            <section className="card">
              <h2 className="card-title">各群の平均と信頼区間</h2>
              <p className="card-hint">
                点が観測された平均、横棒が
                {formatConfidence(1 - analysis.intervalAlpha)}
                信頼区間です。区間は自由度 n − 1 の t 分布で求めています。
              </p>
              <IntervalChart
                caption="各群の平均と信頼区間"
                rows={meanRows(analysis)}
                formatTick={(v) => formatMeasure(v)}
              />
            </section>

            <section className="card">
              <h2 className="card-title">対照群との差と信頼区間</h2>
              <p className="card-hint">
                横棒が 0 の破線をまたいでいる間は「差があるとは言えない」状態です。
              </p>
              <IntervalChart
                caption="対照群との平均の差と信頼区間"
                rows={meanDiffRows(analysis)}
                formatTick={(v) => formatMeasure(v)}
                showZeroLine
              />
            </section>
          </ResultGuard>

          <section className="card">
            <h2 className="card-title">結果の共有</h2>
            <div className="share-row">
              <button type="button" className="button" onClick={share}>
                この結果のリンクをコピー
              </button>
              <button type="button" className="button" onClick={copyReport}>
                Markdown でコピー
              </button>
              {shareStatus && <span className="share-status">{shareStatus}</span>}
            </div>
            <p className="card-hint" style={{ margin: '10px 0 0' }}>
              計算はすべてブラウザ内で行われ、数値がサーバーに送られることはありません。
            </p>
            {fallbackText && (
              <textarea
                className="share-fallback"
                readOnly
                rows={12}
                aria-label="コピーする本文"
                value={fallbackText}
              />
            )}
          </section>
        </>
      )}
    </>
  )
}

interface VerdictProps {
  variant: MeanVariantResult
  controlName: string
  settings: AnalysisSettings
  sequential: SequentialContext | null
  correctionApplied: boolean
}

function MeanVerdictCard({
  variant,
  controlName,
  settings,
  sequential,
  correctionApplied,
}: VerdictProps) {
  const { comparison, significant, mde } = variant
  const verdict = verdictOf(variant)
  const improved = comparison.absoluteDiff > 0
  const tone =
    verdict === 'improved'
      ? 'significant'
      : verdict === 'worsened'
        ? 'negative'
        : 'inconclusive'
  const confidenceLabel = formatPercent(settings.confidence, 0)

  return (
    <div className={`verdict verdict-${tone}`}>
      <div className="verdict-head">
        <span className="verdict-name">
          {variant.group.name} vs {controlName}
        </span>
        <span className="verdict-label">{VERDICT_LABELS[verdict]}</span>
        <span className="verdict-summary">
          {sequential
            ? `${sequential.settings.currentLook} 回目の確認の基準 (p < ` +
              `${formatAlpha(sequential.current.nominalAlpha)}) を` +
              `${significant ? '満たします' : '満たしません'}`
            : significant
              ? `${confidenceLabel} 信頼水準で、差があると言えます`
              : `${confidenceLabel} 信頼水準では、差があるとは言えません`}
        </span>
      </div>

      <div className="metrics">
        <Metric
          label="平均"
          hint={METRIC_HINTS.mean}
          value={formatMeasure(comparison.variant.mean)}
          sub={`対照群 ${formatMeasure(comparison.control.mean)}`}
        />
        <Metric
          label="絶対差"
          hint={METRIC_HINTS.absolute}
          value={formatMeasure(comparison.absoluteDiff, true)}
          sub={formatInterval(comparison.absoluteDiffInterval, (v) =>
            formatMeasure(v, true),
          )}
          tone={improved ? 'positive' : 'negative'}
        />
        <Metric
          label="相対リフト"
          hint={METRIC_HINTS.relative}
          value={formatLift(comparison.relativeLift)}
          sub={formatInterval(comparison.relativeLiftInterval, (v) =>
            formatLift(v),
          )}
          tone={improved ? 'positive' : 'negative'}
        />
        <Metric
          label={correctionApplied ? '補正後 p 値' : 'p 値'}
          hint={METRIC_HINTS.pValue}
          value={formatPValue(
            correctionApplied ? variant.adjustedPValue : variant.rawPValue,
          )}
          sub={
            correctionApplied
              ? `補正前 ${formatPValue(variant.rawPValue)}`
              : comparison.t !== null
                ? `t = ${comparison.t.toFixed(3)}`
                : '計算不能'
          }
        />
        <Metric
          label="自由度"
          hint={METRIC_HINTS.df}
          value={Number.isFinite(comparison.df) ? comparison.df.toFixed(1) : '—'}
          sub="Welch-Satterthwaite"
        />
      </div>

      {!significant && (
        <div className="verdict-note">
          {mde ? (
            <>
              この人数（{formatCount(comparison.control.n)} 件 /{' '}
              {formatCount(comparison.variant.n)} 件）で検出力{' '}
              {formatPercent(settings.diagnosticPower, 0)} を確保できるのは、
              大きさが <strong>{formatMeasure(mde)} 以上</strong>の差までです。
              観測された差は {formatMeasure(comparison.absoluteDiff, true)} なので、
              {Math.abs(comparison.absoluteDiff) < mde
                ? '「差がない」ではなく「この人数では判断できない」状態です。'
                : '効果量は足りていますが、ばらつきの範囲に収まっています。'}
            </>
          ) : (
            <>
              この人数とばらつきでは、検出力{' '}
              {formatPercent(settings.diagnosticPower, 0)} を確保できる差を
              計算できません。入力を確認してください。
            </>
          )}
        </div>
      )}
    </div>
  )
}

function Metric({
  label,
  hint,
  value,
  sub,
  tone,
}: {
  label: string
  hint?: string
  value: string
  sub?: string
  tone?: 'positive' | 'negative'
}) {
  return (
    <div className="metric">
      <div className="metric-label">
        {label}
        {hint && <InfoTip label={label} text={hint} />}
      </div>
      <div className={`metric-value ${tone ? `metric-${tone}` : ''}`}>{value}</div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  )
}

function toneFor(variant: MeanVariantResult): ChartTone {
  const verdict = verdictOf(variant)
  if (verdict === 'inconclusive') return 'variant'
  return verdict === 'improved' ? 'significant' : 'negative'
}

function meanRows(analysis: MeanAnalysisResult): ChartRow[] {
  return [
    {
      label: analysis.control.group.name,
      point: analysis.control.estimate.mean,
      low: analysis.control.estimate.interval.low,
      high: analysis.control.estimate.interval.high,
      tone: 'control' as ChartTone,
    },
    ...analysis.variants.map((variant) => ({
      label: variant.group.name,
      point: variant.comparison.variant.mean,
      low: variant.comparison.variant.interval.low,
      high: variant.comparison.variant.interval.high,
      tone: toneFor(variant),
    })),
  ]
}

function meanDiffRows(analysis: MeanAnalysisResult): ChartRow[] {
  return analysis.variants.map((variant) => ({
    label: variant.group.name,
    point: variant.comparison.absoluteDiff,
    low: variant.comparison.absoluteDiffInterval.low,
    high: variant.comparison.absoluteDiffInterval.high,
    tone: toneFor(variant),
  }))
}
